// MeetTranscriptPage — post-call view for /meet (sub-view of pages/meet.js
// when root.meetView === 'transcript'). Three stacked panels:
//
//   1. SummaryHeader  — 2-3 sentence Gemini-extracted overview + Regenerate
//   2. TranscriptList — chronological utterances (read from root.utterances
//                       which is populated by subscribeMeetRealtime)
//   3. SuggestionsPanel — four buckets of clickable cards. Click → applySuggestion
//                       → existing creation flow on the target feature
//
// Analyze trigger: _maybeAnalyze closure stored on the element, called from
// onCreate AND onUpdate so it re-runs whenever fetch results land or
// realtime adds new utterances. No setInterval — realtime drives state
// transitions naturally.

import {
  SUGGESTION_BUCKETS,
  speakerDisplayName,
  routeTo
} from '../../functions/meet/meetUtils.js'
// migrated 2026-04-27 to SDK route workspaceProject.meet.analysisAppliedItems
import { getSDK } from '../../../shared/functions/sdk.js'

// Normalize the `get_meet_transcript_view` RPC payload into the root
// state shape. Shared by the live fetch transform and the localStorage
// first-paint path so both produce identical state.
const _shapeTranscriptState = (data) => ({
  utterances: (data?.utterances || []).map((r) => ({
    id: r.id,
    roomId: r.room_id,
    speakerEmail: r.speaker_name || '',
    text: r.text || '',
    startedAt: r.started_at,
    endedAt: r.ended_at,
    confidence: r.confidence
  })),
  analysis: data?.analysis || null,
  transcriptLoaded: true
})

// Factory: returns a per-bucket suggestion panel. Uses the NAMED
// `MeetSuggestionCard` component via `childExtends: 'MeetSuggestionCard'`
// (chat's working pattern — inline `childExtends: { ... }` wasn't binding
// onClick in DOMQL v3.14). The bucket name is injected into each child state
// as `meetBucket` (NOT `_bucket` — underscore-prefixed keys get stripped).
// Declared BEFORE MeetTranscriptPage to satisfy JS temporal-dead-zone.
const _section = (bucket) => ({
  flow: 'y',
  gap: 'Z1',
  padding: 'A',
  round: 'A',
  background: 'overlayStrong',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',

  hide: (el, s) => {
    const items = s.root?.analysis?.suggestions?.[bucket]
    return !items?.length
  },

  Header: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',

    Title: {
      tag: 'h3',
      margin: '0',
      fontSize: 'A',
      fontWeight: '600',
      color: 'title',
      text: SUGGESTION_BUCKETS[bucket]?.label || ''
    },
    Count: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => {
        const n = s.root?.analysis?.suggestions?.[bucket]?.length || 0
        return `${n} suggestion${n === 1 ? '' : 's'}`
      }
    }
  },

  Cards: {
    flow: 'y',
    gap: 'Z',
    childExtends: 'MeetSuggestionCard',
    childrenAs: 'state',
    children: (el, s) => {
      const items = s.root?.analysis?.suggestions?.[bucket] || []
      // Attach the bucket name to each child state via a non-underscore
      // field so the card's onClick can dispatch correctly. Also attach
      // a deterministic `id` so smbls can diff the children stably across
      // re-renders (avoids tearing down card DOM on every analysis tick).
      return items.map((item, idx) => ({
        ...item,
        meetBucket: bucket,
        id: `${bucket}-${idx}-${item.title || ''}`
      }))
    }
  }
})

export const MeetTranscriptPage = {
  flow: 'y',
  flex: '1 1 0',
  minHeight: '0',
  overflow: 'auto',
  padding: 'B C',
  gap: 'B',

  // Single-RPC combined fetch: one round-trip returns both utterances and
  // the cached analysis. Backed by `public.get_meet_transcript_view(uuid)`
  // (migration 0032). Saves one REST RTT vs the old two-query pattern.
  //
  // `placeholderData` paints from localStorage on reload — instant first
  // paint (<10ms) while the RPC silently refreshes in the background. Uses
  // `placeholderData` not `initialData` because placeholderData doesn't mark
  // the cache as fresh, so the refetch always runs. `transform` writes fresh
  // results back so the next reload has something to paint from.
  // migrated 2026-04-27 to SDK route workspaceProject.meet.transcriptView (rpc)
  fetch: {
    method: 'rpc',
    from: 'workspaceProject.meet.transcriptView',
    // `as: '~/utterances'` targets root state. When the transform returns an
    // object whose keys include 'utterances', smbls spreads the whole object
    // onto root — so `analysis` and `transcriptLoaded` land on root too.
    as: '~/utterances',
    // Skip while activeRoomId is unseeded — otherwise the RPC errors on
    // the missing uuid param at the REST layer.
    skip: (el, s) => !s.root?.activeRoomId,
    // Wrapper extracts `roomId` (or `p_room_id` for backwards compat) from
    // the params bag — see EntityDispatcher argMap for transcriptView.
    params: (el, s) => ({ roomId: s.root?.activeRoomId, p_room_id: s.root?.activeRoomId }),
    cache: '30s',
    placeholderData: (el, s) => {
      if (typeof localStorage === 'undefined') return undefined
      const rid = s?.root?.activeRoomId
      if (!rid) return undefined
      try {
        const raw = localStorage.getItem(`meet-transcript:${rid}`)
        if (!raw) return undefined
        const data = JSON.parse(raw)
        return _shapeTranscriptState(data)
      } catch { return undefined }
    },
    transform: (data, el, s) => {
      const rid = s?.root?.activeRoomId
      if (rid && typeof localStorage !== 'undefined') {
        try { localStorage.setItem(`meet-transcript:${rid}`, JSON.stringify(data)) }
        catch { /* quota / private mode — non-fatal */ }
      }
      return _shapeTranscriptState(data)
    },
    onFetchComplete: (data, el, s) => {
      if (el._maybeAnalyze) el._maybeAnalyze()
    }
  },

  onCreate: (el, s) => {
    // In DOMQL v3.14 this component is created before the parent page's onCreate
    // seeds activeRoomId, so the declarative `fetch.skip` guard short-circuits
    // on first evaluation. Kick a refetch only if that actually happened —
    // `transcriptLoaded` is set by the transform on success.
    if (s.root?.activeRoomId && !s.root?.transcriptLoaded && el.__ref?.refetch) {
      el.__ref.refetch()
    }

    // Re-scope realtime if landing directly on the transcript view via URL
    // hash (#room=<uuid>&view=transcript). For within-app navigation, onUpdate
    // catches the moment meetView flips to 'transcript'.
    if (s.root?.meetView === 'transcript' && s.root?.activeRoomId) {
      el.call('subscribeMeetRealtime', {
        tables: [
          'meet_rooms',
          'meet_participants',
          'meet_waiting_room',
          'meet_transcripts',
          'meet_transcript_analyses'
        ],
        roomId: s.root.activeRoomId
      })
    }

    // Closure pattern from pages/calendar.js:_tryJoinPresence — called from
    // onCreate once and from onUpdate every time fetch results or realtime
    // events change root state. Bails on no-op transitions.
    el._maybeAnalyze = async () => {
      const root = s.root
      if (!root.activeRoomId) return
      if (root.analysis || root.analyzeError || root._meetAnalyzeInFlight) return
      // Wait for the cached analysis fetch to resolve before hitting the edge
      // function. `analysis` is `undefined` while the fetch is in-flight and
      // `null` once resolved with no row. Firing before resolution produces
      // a redundant round-trip — even though the server returns the cached
      // row cheaply, the extra request is avoidable.
      if (root.analysis === undefined) return
      // Trust the server: analyzeTranscript returns 422 "no transcript
      // available yet" if rows aren't there yet. That maps to analyzeError
      // which the Body factory renders. Don't gate on a client-side filter
      // of root.utterances — that can desync with the real transcript rows
      // when the TranscriptPage fetch races with activeRoomId seeding.
      root.update({
        analyzing: true,
        transcriptPending: false,
        _meetAnalyzeInFlight: true
      })
      try {
        const res = await el.call('analyzeTranscript', root.activeRoomId)
        root.update({
          analyzing: false,
          analysis: res,
          _meetAnalyzeInFlight: false
        })
      } catch (err) {
        const reason = err?.message || 'analysis failed'
        // 422 "no transcript available yet" is expected during a still-running
        // call. Surface it as `transcriptPending` instead of a hard error so
        // the auto-retry loop keeps polling for the transcript worker to land
        // rows. Anything else is a real failure and stops the poll.
        const isPending = err?.status === 422 || /no transcript/i.test(reason)
        root.update({
          analyzing: false,
          _meetAnalyzeInFlight: false,
          transcriptPending: isPending ? true : false,
          analyzeError: isPending ? null : reason
        })
      }
    }
    el._maybeAnalyze()
  },
  onUpdate: (el, s) => {
    // Re-scope realtime to include transcripts whenever this view is active.
    // subscribeMeetRealtime is idempotent (same scope → instant no-op), so
    // calling on every update is safe. Covers within-app navigation and the
    // case where disconnectCall dropped the transcript subscription.
    if (s.root?.meetView === 'transcript' && s.root?.activeRoomId) {
      el.call('subscribeMeetRealtime', {
        tables: [
          'meet_rooms',
          'meet_participants',
          'meet_waiting_room',
          'meet_transcripts',
          'meet_transcript_analyses'
        ],
        roomId: s.root.activeRoomId
      })
    }
    if (el._maybeAnalyze) el._maybeAnalyze()
  },

  // ── Panel 2: Transcript list ─────────────────────────────────────
  Transcript: {
    flow: 'y',
    gap: 'V',
    padding: 'A',
    round: 'A',
    background: 'overlayStrong',
    borderWidth: '1px',
    borderColor: 'line',
    borderStyle: 'solid',
    maxHeight: 'H',
    overflow: 'auto',
    minHeight: 'G',

    CaptionTitle: {
      fontSize: 'Y2',
      Text: { text: 'Transcript' }
    },

    // Three shimmering rows while the fetch is in-flight. Hidden as soon as
    // the transform sets `transcriptLoaded: true` — at that point either the
    // real List takes over or Empty shows.
    Skeleton: {
      hide: (el, s) => !!s.root?.transcriptLoaded,
      flow: 'y',
      gap: 'Z',

      Row_1: { height: 'B', round: 'V', background: 'overlay', opacity: '.6' },
      Row_2: { height: 'B', round: 'V', background: 'overlay', opacity: '.4', width: '80%' },
      Row_3: { height: 'B', round: 'V', background: 'overlay', opacity: '.3', width: '65%' }
    },

    Empty: {
      hide: (el, s) => {
        if (!s.root?.transcriptLoaded) return true
        const u = (s.root?.utterances || []).filter(
          (x) => x.roomId === s.root?.activeRoomId
        )
        return u.length > 0
      },
      tag: 'p',
      margin: '0',
      fontSize: 'Z2',
      color: 'caption',
      text: 'No utterances captured yet.'
    },

    List: {
      hide: (el, s) => !s.root?.transcriptLoaded,
      flow: 'y',
      gap: 'Z',
      childExtends: 'MeetTranscriptUtterance',
      childrenAs: 'state',
      children: (el, s) =>
        (s.root?.utterances || [])
          .filter((u) => u.roomId === s.root?.activeRoomId)
          .sort((a, b) => (a.startedAt || '').localeCompare(b.startedAt || ''))
    }
  },
  // ── Panel 1: Summary header ──────────────────────────────────────
  Summary: {
    flow: 'y',
    gap: 'Z1',
    padding: 'A',
    round: 'A',
    background: 'overlayStrong',
    borderWidth: '1px',
    borderColor: 'line',
    borderStyle: 'solid',

    HeaderRow: {
      flow: 'x',
      align: 'center space-between',
      gap: 'Z1',

      CaptionTitle: {
        fontSize: 'Y2',
        Text: { text: 'Summary' }
      },

      Actions: {
        flow: 'x',
        gap: 'Z',

        SaveAsNoteBtn: {
          hide: (el, s) => !s.root?.analysis?.summary,
          tag: 'button',
          text: (el, s) => {
            // If this summary has already been saved as a note, the
            // applied_items array holds a {bucket:'meet_summary'} entry.
            // Relabel the button so the user knows re-clicking navigates
            // instead of creating a duplicate.
            const applied = s.root?.analysis?.applied_items || []
            const existing = applied.find((a) => a.bucket === 'meet_summary')
            return existing ? '↗ View note' : '＋ Save to notes'
          },
          padding: 'V Z',
          round: 'Z',
          borderWidth: '1px',
          borderColor: 'line',
          borderStyle: 'solid',
          background: 'transparent',
          color: 'title',
          cursor: 'pointer',
          fontSize: 'Y2',
          onClick: async (e, el, s) => {
            const root = s.root
            const roomId = root?.activeRoomId
            const applied = root?.analysis?.applied_items || []

            // Already saved? Just navigate — don't create a duplicate.
            const existing = applied.find((a) => a.bucket === 'meet_summary')
            if (existing) {
              root?.update({ selectedNoteId: existing.target_id })
              routeTo(el, '/documents/notes')
              return
            }

            // First save: create + record in applied_items.
            const room = (root?.rooms || []).find((r) => r.id === roomId)
            const title = (room?.name || 'Call') + ' — summary'
            const content = root?.analysis?.summary || ''
            const noteId = await el.call('createNote', {
              title,
              content,
              tags: ['meet', 'summary'],
              source: 'ai'
            })
            if (!noteId) return

            // Write the meet_summary entry to applied_items via the SDK
            // route. The meet_analysis_update_applied RLS policy allows
            // room members to patch this column.
            // migrated 2026-04-27 to SDK route workspaceProject.meet.analysisAppliedItems
            try {
              const next = [
                ...applied,
                {
                  bucket: 'meet_summary',
                  fingerprint: 'summary',
                  target_id: String(noteId),
                  applied_at: new Date().toISOString()
                }
              ]
              await getSDK().execute(
                'workspaceProject.meet.analysisAppliedItems',
                'update',
                { roomId, appliedItems: next }
              )
              root?.update({
                analysis: { ...root.analysis, applied_items: next }
              })
            } catch (err) {
              console.warn('[SaveAsNoteBtn] applied_items write failed', err)
            }
            routeTo(el, '/documents/notes')
          }
        },

        RegenerateBtn: {
          // Hidden only while an analyze call is in flight. Otherwise it's
          // "Analyze" (no existing analysis) or "Regenerate" (existing one).
          hide: (el, s) => !!s.root?.analyzing,
          tag: 'button',
          text: (el, s) => s.root?.analysis ? 'Regenerate' : 'Analyze',
          padding: 'V Z',
          round: 'Z',
          borderWidth: '1px',
          borderColor: 'line',
          borderStyle: 'solid',
          background: 'transparent',
          color: 'title',
          cursor: 'pointer',
          fontSize: 'Y2',
          onClick: async (e, el, s) => {
            const hadAnalysis = !!s.root?.analysis
            s.root?.update({ analyzing: true, analysis: null, analyzeError: null })
            try {
              const res = await el.call(
                'analyzeTranscript',
                s.root?.activeRoomId,
                hadAnalysis // force=true only when regenerating
              )
              s.root?.update({ analyzing: false, analysis: res })
            } catch (err) {
              s.root?.update({ analyzing: false, analyzeError: err?.message })
            }
          }
        }
      }
    },

    // Two shimmering lines while the cached analysis fetch is in-flight or
    // while the edge function is actively analyzing. Text body stays hidden
    // until we have something real (summary / error / pending / empty) to say.
    Skeleton: {
      hide: (el, s) => {
        const loading = s.root?.analysis === undefined || !!s.root?.analyzing
        return !loading
      },
      flow: 'y',
      gap: 'Z',

      Row_1: { height: 'A', round: 'V', background: 'overlay', opacity: '.6' },
      Row_2: { height: 'A', round: 'V', background: 'overlay', opacity: '.4', width: '70%' }
    },

    Body: {
      hide: (el, s) => s.root?.analysis === undefined || !!s.root?.analyzing,
      tag: 'p',
      margin: '0',
      fontSize: 'Z2',
      color: 'title',
      lineHeight: '1.6',
      text: (el, s) => {
        if (s.root?.analyzeError)
          return `Couldn't analyze: ${s.root?.analyzeError}`
        if (s.root?.transcriptPending)
          return 'Transcription still processing — check back in a moment.'
        return s.root?.analysis?.summary || 'No analysis yet.'
      }
    }
  },

  // ── Panel 3: Suggestions ─────────────────────────────────────────
  Suggestions: {
    hide: (el, s) => !s.root?.analysis,
    flow: 'y',
    gap: 'A',

    CaptionTitle: {
      fontSize: 'Y2',
      Text: { text: 'Apply to other features' }
    },

    // One section per bucket. Each is built via a factory that bakes the
    // bucket name into closures — avoiding the el.parent?.sectionBucket
    // lookup which was unreliable (underscore-prefixed state fields were
    // also getting stripped in child state merging).
    CalendarSection: _section('calendar_events'),
    TicketsSection: _section('tickets'),
    NotesSection: _section('notes'),
    KnowledgeBaseSection: _section('knowledge_base'),
    ChatSection: _section('chat_messages')
  }
}

// One utterance row. Speaker is resolved from root.people so we show
// the display name instead of the raw email the transcriber captured.
export const MeetTranscriptUtterance = {
  flow: 'x',
  gap: 'Z1',
  align: 'flex-start left',
  padding: 'V 0',

  Speaker: {
    tag: 'span',
    minWidth: 'D',
    fontSize: 'Z2',
    fontWeight: '600',
    color: 'cyan',
    text: (el, s) => speakerDisplayName(s.root, s.speakerEmail) + ':'
  },
  Text: {
    tag: 'span',
    flex: '1',
    fontSize: 'Z2',
    color: 'title',
    lineHeight: '1.5',
    text: (el, s) => s.text || ''
  }
}

// _section(bucket) is defined at the top of this file before
// MeetTranscriptPage to satisfy JS temporal-dead-zone rules.
